import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { EMPTY, Observable, tap, catchError } from 'rxjs';
import { API } from '../../constants/api.constants';
import { LoginRequest, LoginResponse } from '../../models/auth.model';
import { TokenService } from '../token/token.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);
  readonly isAuthenticated = this.tokenService.isAuthenticated;

  readonly isLoading = signal(false);

  /** General error message shown under the form. */
  readonly errorMessage = signal<string | null>(null);

  login(credentials: LoginRequest): Observable<LoginResponse> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    return this.http
      .post<LoginResponse>(API.auth.login, credentials, {
        withCredentials: true,
      })
      .pipe(
        tap(response => {
          if (response.status && response.data) {
            this.tokenService.setToken(response.data.token);
            this.tokenService.setUser(response.data.user);
            this.isLoading.set(false);
            this.router.navigate(['/home']);
          }
        }),
        catchError(error => {
          this.isLoading.set(false);

          const message = error.error?.message;
          const errorText =
            typeof message === 'string'
              ? message
              : message?.description || 'An error occurred';

          this.errorMessage.set(errorText);

          return EMPTY;
        })
      );
  }

  logout(): Observable<void> {
    return this.http
      .post<void>(API.auth.logout, {}, { withCredentials: true })
      .pipe(
        tap(() => this.clearSessionState()),
        catchError(() => {
          this.clearSessionState();
          return EMPTY;
        })
      );
  }

  /** Clear all cached state across services to prevent stale data on re-login. */
  private clearSessionState(): void {
    this.tokenService.clearAll();
    window.location.href = '/login';
  }
}
